import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useLanguage } from '@/contexts/LanguageContext';
import { format } from 'date-fns';
import { CalendarIcon, Clipboard, Users, ArrowRight } from 'lucide-react';

interface PasteConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  shiftsCount: number;
  targetDate: Date;
  sourceDate: Date;
  copyMode: 'week' | 'custom' | 'month';
  withGuards: boolean;
  includeGuards: boolean;
}

const PasteConfirmDialog: React.FC<PasteConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  shiftsCount,
  targetDate,
  sourceDate,
  copyMode,
  withGuards,
  includeGuards
}) => {
  const { t } = useLanguage();

  const formatDate = (date: Date) => {
    if (copyMode === 'month') {
      return format(date, 'MMMM yyyy');
    }
    return format(date, 'MMM d, yyyy');
  };

  const getModeText = () => {
    switch (copyMode) {
      case 'week':
        return t("week") || "week";
      case 'month':
        return t("month") || "month";
      case 'custom':
        return t("selectedRange") || "selected range";
      default:
        return "";
    }
  };

  const handleConfirm = () => {
    console.log('PasteConfirmDialog: handleConfirm START');
    
    // Close dialog first to prevent state loops
    onClose();
    console.log('PasteConfirmDialog: Dialog closed first');
    
    // Then execute the action
    try {
      console.log('PasteConfirmDialog: calling onConfirm...');
      onConfirm();
      console.log('PasteConfirmDialog: onConfirm completed successfully');
    } catch (error) {
      console.error('PasteConfirmDialog: Error in confirm callback:', error);
    }
    
    console.log('PasteConfirmDialog: handleConfirm END');
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Clipboard className="h-5 w-5" />
            {t("confirmPaste") || "Confirm Paste"}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-2 flex-1">
                  <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                  <div className="text-xs">
                    <div className="font-medium">{formatDate(sourceDate)} ({getModeText()})</div>
                  </div>
                </div>
                
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
                
                <div className="flex items-center gap-2 flex-1">
                  <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                  <div className="text-xs">
                    <div className="font-medium">{formatDate(targetDate)}</div>
                  </div>
                </div>
              </div>

              <div className="text-center p-2">
                <p className="text-sm font-medium">
                  {shiftsCount} {shiftsCount === 1 ? (t("shift") || "shift") : (t("shifts") || "shifts")}
                </p>
              </div>

              <div className="space-y-2">
                {withGuards && includeGuards && (
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <Users className="h-4 w-4" />
                    <span>{t("withAssignedGuards") || "With assigned guards"}</span>
                  </div>
                )}
                
                {withGuards && !includeGuards && (
                  <div className="flex items-center gap-2 text-sm text-orange-600">
                    <Users className="h-4 w-4" />
                    <span>{t("withoutAssignedGuards") || "Without assigned guards"}</span>
                  </div>
                )}
                
                {!withGuards && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Users className="h-4 w-4" />
                    <span>{t("noGuardsInOriginal") || "No guards in original shifts"}</span>
                  </div>
                )}
              </div>

              <p className="text-sm">
                {t("confirmPasteMessage") || `Are you sure you want to paste ${shiftsCount} shifts to ${formatDate(targetDate)}?`}
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onClose}>
            {t("cancel") || "Cancel"}
          </AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm}>
            {t("pasteShifts") || "Paste Shifts"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default PasteConfirmDialog;